import { useMutation, useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { TempAccessTokenManager, TokenManager } from 'src/shared/helpers';
import api from '../api';
import type { ApiErrorResponse, ApiResponse } from '../types';
import { apiDataResponseMapper, apiPayloadMapper } from '../utils';
import { loginResponseMapper, ssoLoginResponseMapper } from './mappers';
import type {
  BackupCodesResponse,
  ChangeDeviceRequestPayload,
  ChangeDeviceResponse,
  ConfigureSetup2FAPayload,
  ConfigureSetup2FAResponse,
  Initialize2FASetupPayload,
  Initialize2FASetupResponse,
  LoginRequestPayload,
  LoginResponse,
  LogoutRequestPayload,
  MicrosoftLoginRequestPayload,
  ssoLoginRequestPayload,
  SSOLoginResponse,
  Verify2FAPayload,
  VerifyPasswordRequestPayload,
  VerifyPasswordResponse,
} from './types';

// Api.
export const loginRequest = async (
  payload: LoginRequestPayload,
): Promise<ApiResponse<LoginResponse>> => {
  const res = await api.post<any, ApiResponse>('/auth/login/', payload);
  res.data = loginResponseMapper(res);
  return res;
};

export const SSOLoginRequest = async (
  payload: ssoLoginRequestPayload,
): Promise<ApiResponse<SSOLoginResponse>> => {
  const res = await api.post<any, ApiResponse>('/auth/login/cognito/', payload);
  res.data = ssoLoginResponseMapper(res);
  return res;
};

export const microsoftLoginRequest = async (
  payload: MicrosoftLoginRequestPayload,
): Promise<ApiResponse<SSOLoginResponse>> => {
  const res = await api.post<
    MicrosoftLoginRequestPayload,
    ApiResponse<SSOLoginResponse>
  >(
    '/auth/login/microsoft/',
    apiPayloadMapper(
      payload,
    ),
  );
  res.data = ssoLoginResponseMapper(res);
  return res;
};

export const logoutRequest = async (
  payload: LogoutRequestPayload,
): Promise<ApiResponse> => await api.post('/auth/logout/', payload);

export const verify2FACode = async (
  payload: Verify2FAPayload,
): Promise<ApiResponse<LoginResponse>> => {
  const accessToken = TokenManager.getToken() ?? TempAccessTokenManager.get();

  const response = await api.post<string, ApiResponse<LoginResponse>>(
    '/auth/verify-2fa/',
    payload,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  return apiDataResponseMapper(response);
};

export const initialize2FASetup = async (
  payload: Initialize2FASetupPayload,
): Promise<ApiResponse<Initialize2FASetupResponse>> => {
  const { elevatedToken } = payload;
  const accessToken =
    elevatedToken ?? TempAccessTokenManager.get() ?? TokenManager.getToken();

  const response = await api.post<ApiResponse<Initialize2FASetupResponse>>(
    '/auth/setup-2fa/',
    apiPayloadMapper({
      changeDevice: !isNil(elevatedToken),
    }),
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  return apiDataResponseMapper(response);
};

export const verifySetup2FA = async (
  payload: ConfigureSetup2FAPayload,
): Promise<ApiResponse<ConfigureSetup2FAResponse>> => {
  const accessToken = TempAccessTokenManager.get() ?? TokenManager.getToken();

  const response = await api.post<
    ConfigureSetup2FAPayload,
    ApiResponse<ConfigureSetup2FAResponse>
  >('/auth/configure-2fa-setup/', payload, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return apiDataResponseMapper(response);
};

export const verifyPassword = async (
  payload: VerifyPasswordRequestPayload,
): Promise<ApiResponse<VerifyPasswordResponse>> => {
  const response = await api.post<
    VerifyPasswordRequestPayload,
    ApiResponse<VerifyPasswordResponse>
  >('/auth/verification/', payload);

  return apiDataResponseMapper(response);
};

export const getBackupCodes = async (
  elevatedToken: string,
): Promise<ApiResponse<BackupCodesResponse>> => {
  const response = await api.get<
    BackupCodesResponse,
    ApiResponse<BackupCodesResponse>
  >('/auth/backup-codes/', {
    headers: { Authorization: `Bearer ${elevatedToken}` },
  });

  return apiDataResponseMapper(response);
};

export const regenerateBackupCodes = async (
  elevatedToken: string,
): Promise<ApiResponse<BackupCodesResponse>> => {
  const response = await api.post<ApiResponse<BackupCodesResponse>>(
    '/auth/backup-codes/',
    {},
    { headers: { Authorization: `Bearer ${elevatedToken}` } },
  );

  return apiDataResponseMapper(response);
};

export const changeDevice = async (
  payload: ChangeDeviceRequestPayload,
): Promise<ApiResponse<ChangeDeviceResponse>> => {
  const { code, elevatedToken } = payload;
  const response = await api.post<
    ChangeDeviceRequestPayload,
    ApiResponse<ChangeDeviceResponse>
  >(
    '/auth/change-device/',
    { code },
    { headers: { Authorization: `Bearer ${elevatedToken}` } },
  );

  return apiDataResponseMapper(response);
};

// Hooks.
export const useLoginRequest = () =>
  useMutation<ApiResponse<LoginResponse>, ApiErrorResponse, any>({
    mutationFn: async (payload: LoginRequestPayload) => loginRequest(payload),
  });

export const useSSOLoginRequest = () =>
  useMutation<ApiResponse<SSOLoginResponse>, ApiErrorResponse, any>({
    mutationFn: async (payload: ssoLoginRequestPayload) =>
      SSOLoginRequest(payload),
  });

export const useMicrosoftLogin = () =>
  useMutation<
    ApiResponse<SSOLoginResponse>,
    ApiErrorResponse,
    MicrosoftLoginRequestPayload
  >({
    mutationFn: async (payload: MicrosoftLoginRequestPayload) =>
      microsoftLoginRequest(payload),
  });

export const useLogoutRequest = () =>
  useMutation<ApiResponse, ApiErrorResponse, any, LogoutRequestPayload>({
    mutationFn: async (payload: LogoutRequestPayload) => logoutRequest(payload),
  });

export const useVerify2FACode = () =>
  useMutation<ApiResponse<LoginResponse>, ApiErrorResponse, Verify2FAPayload>({
    mutationFn: verify2FACode,
  });

export const useInitialize2FASetup = () =>
  useMutation<
    ApiResponse<Initialize2FASetupResponse>,
    ApiErrorResponse,
    Initialize2FASetupPayload
  >({
    mutationFn: initialize2FASetup,
  });

export const useVerifySetup2FA = () =>
  useMutation<
    ApiResponse<ConfigureSetup2FAResponse>,
    ApiErrorResponse,
    ConfigureSetup2FAPayload
  >({
    mutationFn: verifySetup2FA,
  });

export const useVerifyPassword = () =>
  useMutation<
    ApiResponse<VerifyPasswordResponse>,
    ApiErrorResponse,
    VerifyPasswordRequestPayload
  >({
    mutationFn: verifyPassword,
  });

export const useBackupCodesList = (elevatedToken: string, enabled: boolean) =>
  useQuery<ApiResponse<BackupCodesResponse>, ApiErrorResponse>({
    queryKey: ['backup-codes', elevatedToken],
    queryFn: () => getBackupCodes(elevatedToken),
    enabled: enabled && !!elevatedToken,
  });

export const useRegenerateBackupCodes = () =>
  useMutation<ApiResponse<BackupCodesResponse>, ApiErrorResponse, string>({
    mutationFn: regenerateBackupCodes,
  });

export const useChangeDevice = () =>
  useMutation<
    ApiResponse<ChangeDeviceResponse>,
    ApiErrorResponse,
    ChangeDeviceRequestPayload
  >({
    mutationFn: changeDevice,
  });
